import { useMemo } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { IQ_TOOL_ID, MANAGED_TOOLS, type ManagedTool } from '@/shared/config/managedTools';
import { useIqAllowed } from '@/shared/config/useIqAllowed';
import { useManagedToolAccess } from '@/shared/config/useManagedToolAccess';

/**
 * Returns the managed tools (Content Test, Focus Groups, NACB Trigger Intake,
 * IQ) currently visible to the user, applying the SAME feature-flag +
 * permission gating the tools page (`ToolGrid`) uses. These tools are synthetic
 * (not returned by `getAllAgents`), so any surface that lists tools by category
 * must add them explicitly. Centralizing the gating here keeps the top dropdown
 * and the tools grid from drifting apart — the bug where NACB showed on the
 * grid but not in the dropdown.
 */
export const useVisibleManagedTools = (): ManagedTool[] => {
  const workspaceId = useCurrentWorkspaceId();
  const { canOpen } = useManagedToolAccess();
  const { isAllowed: isIqAllowed, isLoading: isIqLoading } = useIqAllowed();

  const isNACBIntakeEnabled = useBooleanFlagValue('nacb_trigger_intake', false);

  return useMemo(() => {
    if (!workspaceId) {
      return [];
    }

    // Mirror ToolGrid's per-tool gating exactly. IQ has no feature flag, so
    // `useIqAllowed` is both of its halves.
    const isVisible: Record<string, boolean> = {
      'test-content': canOpen('test-content'),
      'nacb-intake': isNACBIntakeEnabled && canOpen('nacb-intake'),
      'focus-groups': canOpen('focus-groups'),
      [IQ_TOOL_ID]: isIqAllowed && !isIqLoading,
    };

    return MANAGED_TOOLS.filter((tool) => isVisible[tool.id] ?? false);
  }, [workspaceId, isNACBIntakeEnabled, canOpen, isIqAllowed, isIqLoading]);
};
